import { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getParcelsByExploitation } from 'services/API/Exploitation';
import { launch_calculate } from 'services/API/Simulation';
import { ExploitationContext } from 'utils/context';
import { getColorById } from 'utils/culturesColors';
import { displayErrorMessage } from 'utils/tools_functions';
import {
  decodeData,
  STORAGE_KEY,
  saveAllSimulations,
  getAllSimulations,
} from '../../simulationConfig.hook';
import { sortByCAPCategory, sortCultures } from '../utils/cultureSort';

const sortSimulationData = (simulationData) => {
  if (!simulationData) return simulationData;

  if (simulationData.previous?.culturesDistribution) {
    simulationData.previous.culturesDistribution = [
      ...simulationData.previous.culturesDistribution,
    ].sort((a, b) => sortByCAPCategory(a.culture, b.culture));
  }

  if (simulationData.suggestions) {
    Object.values(simulationData.suggestions).forEach((suggestion) => {
      suggestion.years?.forEach((year) => {
        if (year.culturesDistribution) {
          year.culturesDistribution = [...year.culturesDistribution].sort((a, b) =>
            sortByCAPCategory(a.culture, b.culture),
          );
        }
      });
    });
  }

  const firstSuggestion = Object.values(simulationData.suggestions ?? {})[0];
  const allDistributions = firstSuggestion?.years?.[0]?.culturesDistribution ?? [];

  simulationData.cultures = sortCultures(
    simulationData.cultures.map((c) => {
      const distribution =
        simulationData.previous.culturesDistribution.find((d) => d.culture?.id === c.id) ??
        allDistributions.find((d) => d.culture?.id === c.id);

      return {
        ...c,
        capCategory: distribution?.culture?.capCategory ?? c.capCategory,
      };
    }),
  );
  return simulationData;
};

const useResultData = (exploitation, setExploitation, simulationResult) => {
  const { t } = useTranslation();
  const { culturesColors } = useContext(ExploitationContext);

  const [result, setResult] = useState(null);
  const [suggest, setSuggest] = useState(null);
  const [exploitationParcels, setExploitationParcels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exploitationSelected, setExploitationSelected] = useState(exploitation);
  const [coordsExist, setCoordsExist] = useState(false);

  const [pieChartData, setPieChartData] = useState([]);
  const [interculturePieChartData, setInterculturePieChartData] = useState([]);
  const [totalExploitationSurface, setTotalExploitationSurface] = useState(0);
  const [grossMarginTableData, setGrossMarginTableData] = useState(null);
  const [lineChartData, setLineChartData] = useState([]);

  const [simulationHistory, setSimulationHistory] = useState([]);
  const [selectedSimulationId, setSelectedSimulationId] = useState('');
  const [currentSimulationResult, setCurrentSimulationResult] = useState(simulationResult);

  const [selectedSuggestionKey, setSelectedSuggestionKey] = useState('suggestion1');
  const [hasChange, setHasChange] = useState(false);

  const editedSynthesisCellsRef = useRef([]);
  const currentSimulationResultRef = useRef(currentSimulationResult);
  const forecastCampaign = exploitationSelected?.forecastCampaign;

  useEffect(() => {
    currentSimulationResultRef.current = currentSimulationResult;
  }, [currentSimulationResult]);

  useEffect(() => {
    loadSimulationHistory();
  }, []);

  useEffect(() => {
    if (simulationResult) {
      setCurrentSimulationResult(simulationResult);
      setSelectedSimulationId('');
      loadSimulationHistory();
    }
  }, [simulationResult]);

  useEffect(() => {
    if (!currentSimulationResult) return;
    if (!exploitationSelected) {
      setExploitation(false).then((res) => {
        const exploit = res.data.data;
        if (exploit) {
          setExploitationSelected(exploit);
          fetchSimulationResult(exploit);
        }
      });
    } else {
      fetchSimulationResult(exploitationSelected);
    }
  }, [currentSimulationResult]);

  useEffect(() => {
    if (suggest) {
      prepareChartData();
    }
  }, [suggest]);

  const loadSimulationHistory = () => {
    try {
      const storedData = JSON.parse(localStorage.getItem(STORAGE_KEY))?.data;
      if (!storedData || !exploitationSelected?.id) return;

      const parsed = decodeData(storedData)?.[exploitationSelected.id];
      if (!parsed) return;

      const allSimulations = [];

      if (parsed.current?.params) {
        allSimulations.push({
          ...parsed.current,
          label: `${t('simulation.result.history.current')} - ${formatDate(parsed.current.date)}`,
          isCurrent: true,
        });
      }

      if (parsed.history?.length > 0) {
        [...parsed.history].reverse().forEach((simu) => {
          if (simu?.params) {
            allSimulations.push({
              ...simu,
              label: formatDate(simu.date),
              isCurrent: false,
            });
          }
        });
      }

      setSimulationHistory(allSimulations);

      if (parsed.current?.jobId && !selectedSimulationId) {
        setSelectedSimulationId(parsed.current.jobId);
      }
    } catch (error) {
      console.error(t('simulation.errors.loadHistory'), error);
    }
  };

  const removeSimulationHistory = (jobId) => {
    const allSimulation = getAllSimulations();
    const removedHistory = simulationHistory.filter((s) => s.jobId !== jobId);

    if (selectedSimulationId === jobId) {
      const firstRemaining = removedHistory[0];
      setSelectedSimulationId(firstRemaining?.jobId ?? '');
      setCurrentSimulationResult(
        firstRemaining ? { config: firstRemaining.config, result: firstRemaining.result } : null,
      );
    }

    setSimulationHistory(removedHistory);
    allSimulation[exploitation.id].history = removedHistory.filter((s) => !s.isCurrent);
    saveAllSimulations(allSimulation);
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('simulation.result.history.unknownDate');
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return t('simulation.result.history.unknownDate');
    }
  };

  const handleSelectSimulation = (event) => {
    const jobId = event.target.value;
    setSelectedSimulationId(jobId);

    if (!jobId) return;

    const selectedSimu = simulationHistory.find((s) => s.jobId === jobId);
    if (selectedSimu?.params) {
      setCurrentSimulationResult({
        config: selectedSimu.params,
        result: selectedSimu.result,
      });
    }
  };

  const fetchSimulationResult = async (exploit) => {
    try {
      setLoading(true);
      const simulationData = currentSimulationResultRef.current?.result;

      sortSimulationData(simulationData);

      setResult(simulationData);

      const suggestionKeys = Object.keys(simulationData?.suggestions || {});
      const initialKey = suggestionKeys.includes(selectedSuggestionKey)
        ? selectedSuggestionKey
        : suggestionKeys[0] || 'suggestion1';

      setSelectedSuggestionKey(initialKey);
      setSuggest(simulationData?.suggestions?.[initialKey]);

      const parcelsResponse = await getParcelsByExploitation(exploit?.id);
      setExploitationParcels(parcelsResponse.data.data);
      setCoordsExist(parcelsResponse.data.data.some((p) => p?.coordinates?.length > 0));
    } catch (error) {
      console.error('Error fetching simulation result:', error);
      toast.error(displayErrorMessage('ERR_GET_SIMULATION_RESULT'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSuggestion = (key) => {
    setSelectedSuggestionKey(key);
    setSuggest(result?.suggestions?.[key]);
  };

  const launchCalculette = async () => {
    const repartitionIrrigation = [];
    suggest.parcels.forEach((p, idx) => {
      const irrigationPerParcelAndYears = [];
      suggest.years.forEach((y) => {
        irrigationPerParcelAndYears.push([y.irrigationDistribution[idx]]);
      });
      repartitionIrrigation.push(irrigationPerParcelAndYears);
    });

    const body = {
      parcels: suggest.parcels,
      repartitionIrrigation,
    };

    try {
      const res = await launch_calculate(body, exploitationSelected?.id);
      editedSynthesisCellsRef.current = [];
      const newResult = res.data.data;
      newResult.previous = result.previous;
      sortSimulationData(newResult);

      const updatedSuggestions = {
        ...result.suggestions,
        [selectedSuggestionKey]: newResult.result,
      };
      newResult.suggestions = updatedSuggestions;
      newResult.objectives = result.objectives;

      setResult(newResult);
      setSuggest(newResult.suggestions[selectedSuggestionKey]);
      setHasChange(false);

      toast.success(t('simulation.result.messages.recalculateSuccess'));
    } catch (error) {
      const errorToDisplay = error?.response?.data || null;
      if (errorToDisplay?.errors) {
        for (const err of errorToDisplay.errors) {
          toast.error(displayErrorMessage(err.err_code, err.message), { autoClose: false });
        }
      } else {
        toast.error(t('simulation.result.messages.recalculateError'));
      }
    }
  };

  const prepareChartData = () => {
    if (!suggest?.years) return;

    const previousYear = {
      grossMargin: result?.previous?.grossMargin?.[0] || 0,
      culturesDistribution: result?.previous?.culturesDistribution || [],
      revenue: result?.previous?.revenue?.[0] || 0,
    };

    const years = [previousYear, ...suggest.years];

    preparePieCharts(years);
    prepareEvolutionData(years);
  };

  const prepareDistributionData = (years, type = 'main') => {
    if (!years?.length) return [];

    return years.map((yearData, i) => {
      const year = forecastCampaign.startYear + i - 1;

      if (!yearData?.culturesDistribution?.length) {
        return { year, data: [] };
      }

      const pieYear = yearData.culturesDistribution
        .filter(({ hectares, culture }) => hectares > 0 && culture.type === type)
        .map(({ culture, hectares, grossMargin }) => {
          const color = '#' + getColorById(culture.id, culturesColors) || '#999';
          const margin = grossMargin ?? 0;
          const marginPerHectare = hectares > 0 ? margin / hectares : 0;
          return {
            id: culture.name,
            label: culture.name,
            value: parseFloat(hectares.toFixed(2)),
            color,
            grossMargin: parseFloat(margin.toFixed(2)),
            marginPerHectare: parseFloat(marginPerHectare.toFixed(2)),
            hectares: parseFloat(hectares.toFixed(2)),
            capCategory: culture.capCategory,
          };
        })
        .sort(type === 'main' ? sortByCAPCategory : (a, b) => b.hectares - a.hectares);

      return { year, data: pieYear };
    });
  };

  const preparePieCharts = (years) => {
    const mainData = prepareDistributionData(years, 'main');
    setPieChartData(mainData);

    const intercultureData = prepareDistributionData(years, 'intermediate');
    setInterculturePieChartData(intercultureData);

    const totalSurface = suggest?.parcels?.reduce((sum, p) => sum + (p.surface || 0), 0) || 0;
    setTotalExploitationSurface(totalSurface);
  };

  const prepareEvolutionData = (years) => {
    const tableData = {
      campaigns: [],
      grossMargin: [],
      revenue: [],
      charges: [],
    };
    const marginData = [];
    const revenueData = [];

    years.forEach((yearData, index) => {
      const year = forecastCampaign.startYear + index - 1;
      const grossMargin = yearData.grossMargin || 0;

      const revenue = yearData.revenue || 0;

      const charges = +(revenue - grossMargin).toFixed(0);

      const prevYearData = index > 0 ? years[index - 1] : null;
      const previousGrossMargin = prevYearData?.grossMargin || 0;
      const previousRevenue = prevYearData?.revenue || 0;
      const previousCharges = +(previousRevenue - previousGrossMargin).toFixed(0);

      tableData.campaigns.push(`${year}-${year + 1}`);
      tableData.grossMargin.push({
        value: grossMargin,
        variation:
          index > 0 && previousGrossMargin !== 0
            ? ((grossMargin - previousGrossMargin) / Math.abs(previousGrossMargin)) * 100
            : 0,
      });
      tableData.revenue.push({
        value: revenue,
        variation:
          index > 0 && previousRevenue !== 0
            ? ((revenue - previousRevenue) / Math.abs(previousRevenue)) * 100
            : 0,
      });
      tableData.charges.push({
        value: charges,
        variation:
          index > 0 && previousCharges !== 0
            ? ((charges - previousCharges) / Math.abs(previousCharges)) * 100
            : 0,
      });

      marginData.push({ x: year, y: grossMargin });
      revenueData.push({ x: year, y: revenue });
    });

    setGrossMarginTableData(tableData);
    setLineChartData([
      { id: t('simulation.result.keyNumbers.grossMargin'), color: '#4CAF50', data: marginData },
      { id: t('simulation.result.keyNumbers.revenue'), color: '#2196F3', data: revenueData },
    ]);
  };

  return {
    result,
    setResult,
    suggest,
    setSuggest,
    exploitationParcels,
    exploitationSelected,
    loading,
    coordsExist,
    forecastCampaign,

    pieChartData,
    interculturePieChartData,
    totalExploitationSurface,
    grossMarginTableData,
    lineChartData,

    simulationHistory,
    selectedSimulationId,
    currentSimulationResult,
    handleSelectSimulation,
    removeSimulationHistory,

    selectedSuggestionKey,
    handleSelectSuggestion,
    launchCalculette,
    hasChange,
    setHasChange,

    editedSynthesisCellsRef,
  };
};

export default useResultData;
